import React, {
  memo,
  useState,
  useEffect,
  useCallback,
  createRef,
  ReactElement,
  KeyboardEvent,
  MouseEvent,
} from 'react'
import classnames from 'classnames'
import uuid from 'uuid'
import { DownArrowIcon, LeftArrowIcon } from 'transfix-icons'
import { KEY_VALUES } from 'constants/keyValues'
import { Button } from '..'
import 'scss/5_components/_expandable-row.scss'

const BODY_CLASS_NAME = 'expandable-row-open'
const OPEN_CLASS_NAME = 'expandable-row-expanded'
const INTERACTIVE_TAGS = ['button', 'a', 'input']
const HANDLE_KEYS = [KEY_VALUES.ENTER, KEY_VALUES.SPACE, KEY_VALUES.SPACEBAR].map(key =>
  key.toLowerCase()
)

export interface IExpandableRowProps {
  /**
   * ID of the expandable row.
   *
   * @default uuid()
   */
  id?: string
  /**
   * Content of the row, clicking the row will expand it below this content.
   */
  row: ReactElement
  /**
   * Content of the expanded part of the row.  This is what gets displayed below the row element.
   */
  details: ReactElement
  /**
   * The state of whether the expanded row is expanded.
   *
   * @default false
   */
  expanded?: boolean
  /**
   * Specifies whether or not DPL styles should be applied to the row.
   *
   * @default true
   */
  dpl?: boolean
  /**
   * The event to be fired upon expansion of the row.
   *
   * @default () => {}
   */
  onExpand?: () => void
  /**
   * Determines whether we should apply the keepGutters class to the container of the row.
   *
   * @default false
   */
  keepGutters?: boolean
  /**
   * Specifies any additional classes to be added to the clickable portion of the row.
   *
   * @default ''
   */
  rowClass?: string
  /**
   * Specifies any additional classes to be added to the details section.
   *
   * @default ''
   */
  detailsClass?: string
  /**
   * Optional - add class for expandable row
   *
   * @default ''
   */
  expandableRowClassProp?: string
  /**
   * Optional - add class for details pane
   *
   * @default ''
   */
  paneClassProp?: string
}

const setGlobalStyle = (expanded: boolean) => {
  if (expanded) {
    global.document.body.classList.add(BODY_CLASS_NAME)
  } else if (document.querySelectorAll(`.${OPEN_CLASS_NAME}`).length === 1) {
    global.document.body.classList.remove(BODY_CLASS_NAME)
  }
}

export const ExpandableRow = memo<IExpandableRowProps>(
  ({
    id = uuid(),
    row,
    details,
    expanded: expandedProp = false,
    dpl = true,
    onExpand = () => {},
    keepGutters = false,
    rowClass = '',
    detailsClass = '',
    expandableRowClassProp = '',
    paneClassProp = '',
  }) => {
    const [expanded, setExpanded] = useState(expandedProp)
    const paneRef = createRef<HTMLDivElement>()

    const toggle = useCallback(
      (overrideExpand = undefined) => {
        const newExpandVal = typeof overrideExpand === 'boolean' ? overrideExpand : !expanded
        if (newExpandVal && onExpand) onExpand()
        setExpanded(newExpandVal)
        if (dpl) {
          setGlobalStyle(newExpandVal)
        }
      },
      [dpl, expanded, onExpand]
    )

    useEffect(() => {
      setExpanded(expandedProp)
    }, [expandedProp])

    useEffect(() => {
      return () => {
        setGlobalStyle(false)
      }
    }, [])

    const onRowClickHandler = ({ target }: MouseEvent) => {
      const selectedTarget = target as HTMLElement
      if (dpl && !INTERACTIVE_TAGS.includes(selectedTarget.tagName.toLowerCase())) {
        toggle()
      }
    }

    const onKeyDownHandler = ({ key }: KeyboardEvent) => {
      const validKeyPress = !key || HANDLE_KEYS.includes(key.toLowerCase())
      if (dpl && validKeyPress) {
        toggle()
      }
    }

    // for expandableRowClass and paneClass, the following names will be applied
    // if the user does not pass an arg for those props
    const expandableRowClass = classnames(
      expandableRowClassProp !== ''
        ? expandableRowClassProp
        : {
            'expandable-row': dpl,
            'expandable-row-legacy': !dpl,
            [OPEN_CLASS_NAME]: expanded,
            'keep-gutters': keepGutters,
          }
    )
    const paneClass = classnames(paneClassProp !== '' ? paneClassProp : 'expandable-row-pane')

    const mobileBackButtonClass = classnames('pa2', {
      'show-on-small': expanded,
    })
    const arrowDownClass = classnames('icon medium text-secondary rotate-transition', {
      rotate: expanded,
    })

    return (
      <div className={expandableRowClass}>
        {Boolean(dpl && expanded) && (
          <div className={mobileBackButtonClass}>
            <Button onClick={toggle} color='tertiary' startIcon={<LeftArrowIcon />}>
              Back to list
            </Button>
          </div>
        )}
        <div
          data-test={`expandable-row-${id}`}
          className={`row-target flex items-center ${rowClass} ph5 pv3`}
          onClick={onRowClickHandler}
          onKeyDown={onKeyDownHandler}
          role='button'
          tabIndex={-1}>
          <span
            aria-label='Expand row'
            className='pointer icon-hoverable'
            role='button'
            tabIndex={0}
            onClick={toggle}
            onKeyPress={toggle}
            data-test={`expandable-toggle-${id}`}>
            <DownArrowIcon className={arrowDownClass} />
          </span>
          <div className='flex-grow-1'>{row}</div>
        </div>
        {expanded && (
          <div data-test={`expandable-details-${id}`} className={paneClass} ref={paneRef}>
            <div className={detailsClass}>{details}</div>
          </div>
        )}
      </div>
    )
  }
)

export default ExpandableRow
